<template>
    <div class="w-full p-0">
        <div class="center ml-0 px-2 py-4">
            <div class="grid">
                <aside class="col-12 xl:col-2 lg:col-2 md:col-12 sm:col-12">
                    <div class="w-full flex flex-column gap-2">
                        <router-link
                            to="/"
                            class="flex surface-0 border-1 border-100 border-round-lg p-3 no-underline"
                            :class="currentTag ? 'text-500 hover:text-primary' : 'text-primary border-primary'"
                        >
                            <span class="text-lg font-medium">Все статьи</span>
                        </router-link>

                        <router-link
                            v-for="tagStat in tags"
                            :key="tagStat.tag"
                            :to="getTagRoute(tagStat.tag)"
                            class="flex surface-0 border-1 border-100 border-round-lg p-3 no-underline"
                            :class="currentTag === tagStat.tag ? 'text-primary border-primary' : 'text-500 hover:text-primary'"
                        >
                            <span class="text-sm font-medium">{{ formatTagLabel(tagStat.tag) }} ({{ tagStat.count }})</span>
                        </router-link>
                    </div>
                </aside>

                <main id="feed" class="col-12 xl:col-7 lg:col-7 md:col-12 sm:col-12">
                    <section v-if="isLoading" class="w-full surface-0 border-1 border-100 px-3 py-4 border-round-2xl">
                        <Skeleton class="w-hull mb-3" height="2rem" />
                    </section>

                    <section v-else-if="errorMessage" class="w-full surface-0 border-1 border-100 px-3 py-4 border-round-2xl text-red-500">
                        {{ errorMessage }}
                    </section>

                    <section v-else-if="articles.length" class="w-full flex flex-column gap-3">
                        <article
                            v-for="article in articles"
                            :key="article.slug"
                            class="w-full surface-0 border-1 border-100 px-3 py-4 border-round-2xl cursor-pointer hover:border-200"
                        >
                            <div class="w-full flex flex-row justify-content-between mb-3">
                                <div>
                                    <div class="text-sm text-500">Опубликовано {{ formatDate(article.publishedAt) }}</div>
                                </div>
                                <Tag v-if="article.country" severity="info" class="text-xs">{{ formatTagLabel(article.country) }}</Tag>
                            </div>

                            <!-- <img v-if="article.cover?.url" :src="article.cover.url" :alt="article.cover.alt || article.title" class="w-full border-round-xl mb-3" /> -->

                            <router-link :to="getArticleRoute(article)" class="text-900 no-underline">
                                <h2 class="text-2xl font-medium text-900">{{ article.title }}</h2>
                                <p class="text-normal text-700 mt-2">
                                    {{ getArticlePreview(article) }}
                                </p>
                            </router-link>

                            <div class="w-full flex flex-row justify-content-between mt-3">
                                <div class="text-sm text-500 flex flex-row gap-3">
                                    <span>{{ getReadingTime(article) }} мин</span>
                                    <span v-if="article.tags?.length">{{ getBlockCount(article) }} тегов</span>
                                </div>
                                <router-link :to="getArticleRoute(article)" class="text-sm text-500 no-underline">
                                    Читать
                                </router-link>
                            </div>
                        </article>

                        <div v-if="shouldShowPagination" class="w-full surface-0 border-1 border-100 px-2 py-2 border-round-2xl">
                            <Paginator
                                :rows="pagination.limit"
                                :totalRecords="pagination.total"
                                :first="paginationFirst"
                                template="PrevPageLink PageLinks NextPageLink"
                                @page="handlePageChange"
                            />
                        </div>
                    </section>

                    <section v-else class="w-full surface-0 border-1 border-100 px-3 py-4 border-round-2xl">
                        Статьи по текущему фильтру не найдены.
                    </section>
                </main>

                <!-- <aside id="digest" class="col-12 xl:col-3 lg:col-3 md:col-12 sm:col-12 flex flex-column gap-3">
                    <div class="w-full surface-0 border-1 border-100 px-3 py-4 border-round-2xl">
                        <span class="blog-panel__label">Подборка дня</span>
                        <h2 class="blog-panel__title">Как настроить Apple Wallet без лишних шагов</h2>
                        <p class="blog-panel__text">
                            На правой колонке держим короткий редакционный анонс и служебные карточки, как в медиа-лентах.
                        </p>
                    </div>
                </aside> -->
            </div>
        </div>
    </div>
</template>

<script>
import Connect from '@/assets/js/Connect'
import { buildArticleRoute } from '@/assets/js/blog/articleRoute'

const connect = new Connect()

export default {
    name: 'BlogListPage',
    PAGE_SIZE: 10,
    data() {
        return {
            articles: [],
            tags: [],
            isLoading: false,
            errorMessage: '',
            pagination: {
                page: 1,
                limit: 10,
                total: 0,
                totalPages: 1,
            },
        }
    },
    computed: {
        currentTag() {
            return this.$route.query.tag || ''
        },
        currentPage() {
            const page = Number.parseInt(this.$route.query.page, 10)

            return Number.isInteger(page) && page > 0 ? page : 1
        },
        paginationFirst() {
            return (this.pagination.page - 1) * this.pagination.limit
        },
        shouldShowPagination() {
            return this.pagination.total > this.pagination.limit
        },
        routeStateKey() {
            return `${this.currentTag}:${this.currentPage}`
        },
    },
    watch: {
        routeStateKey: {
            immediate: true,
            async handler() {
                this.updatePageTitle()
                await this.fetchArticles()
            },
        },
    },
    async created() {
        await this.fetchTags()
    },
    methods: {
        // Список и фильтры теперь приходят с backend blog API.
        async fetchArticles() {
            this.isLoading = true
            this.errorMessage = ''

            const response = await connect.GetArticles({
                tag: this.currentTag,
                page: this.currentPage,
                limit: this.$options.PAGE_SIZE,
            })

            if (response?.result) {
                this.articles = response.data || []
                this.syncPagination(response.meta?.pagination)
            } else {
                this.articles = []
                this.errorMessage = response?.message || 'Не удалось загрузить список статей.'
                this.syncPagination()
            }

            this.isLoading = false
        },
        // Держим UI пагинации синхронизированным с backend meta.pagination.
        syncPagination(metaPagination = null) {
            const limit = Number(metaPagination?.limit) || this.$options.PAGE_SIZE

            this.pagination = {
                page: Number(metaPagination?.page) || this.currentPage,
                limit,
                total: Number(metaPagination?.total) || 0,
                totalPages: Number(metaPagination?.totalPages) || 1,
            }
        },
        async fetchTags() {
            const response = await connect.GetArticleTags()

            if (response?.result) {
                this.tags = response.data || []
            } else {
                this.tags = []
            }
        },
        formatDate(value) {
            if (!value) {
                return 'не указано'
            }

            return new Intl.DateTimeFormat('ru-RU', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            }).format(new Date(value))
        },
        updatePageTitle() {
            document.title = this.currentTag ? `Блог / #${this.currentTag} | Aasia` : 'Блог | Aasia'
        },
        getArticlePreview(article) {
            return article.excerpt || 'Откройте статью, чтобы увидеть полную инструкцию.'
        },
        getArticleRoute(article) {
            return buildArticleRoute(article)
        },
        getTagRoute(tag) {
            return tag ? { path: '/', query: { tag } } : { path: '/' }
        },
        handlePageChange(event) {
            const page = (event.page || 0) + 1

            this.$router.push({
                path: '/',
                query: {
                    ...(this.currentTag ? { tag: this.currentTag } : {}),
                    ...(page > 1 ? { page: String(page) } : {}),
                },
            })
        },
        getBlockCount(article) {
            return article.tags?.length || 0
        },
        getReadingTime(article) {
            return article.readingTime || 1
        },
        formatTagLabel(tag) {
            return `#${tag}`
        },
    },
}
</script>
